import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type { Permission, User } from './types';

type AuthState = { user: User | null; loading: boolean; login: (email: string, password: string) => Promise<void>; logout: () => Promise<void> };
const Ctx = createContext<AuthState>({ user: null, loading: true, login: async () => {}, logout: async () => {} });
export const useAuth = () => useContext(Ctx);

/**
 * Whether the signed-in user may do something (admins may do everything). Used to hide
 * actions the user cannot perform; the server enforces the same rules on every request.
 */
export function useCan() {
  const { user } = useAuth();
  return (need: Permission | 'admin') =>
    Boolean(
      user &&
        (user.role === 'admin' ||
          (need !== 'admin' && (user.permissions?.includes(need) || (need === 'orders.pick' && user.permissions?.includes('orders.edit')) ||
            (need === 'inventory.count' && user.permissions?.includes('inventory.edit'))))),
    );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  useEffect(() => {
    api<{ user: User }>('/api/auth/me')
      .then((r) => setUser(r.user))
      .catch((e) => {
        if (!(e instanceof ApiError && e.status === 401)) console.error(e);
        setUser(null);
      })
      .finally(() => setLoading(false));
    // Any 401 from the API (expired session) drops back to the login screen.
    const onUnauthorized = () => {
      setUser(null);
      qc.clear();
    };
    window.addEventListener('markit:unauthorized', onUnauthorized);
    return () => window.removeEventListener('markit:unauthorized', onUnauthorized);
  }, [qc]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await api<{ user: User }>('/api/auth/login', { body: { email, password } });
    setUser(r.user);
  }, []);
  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    qc.clear();
  }, [qc]);

  return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>;
}
